import { useEffect, useCallback } from 'react';

import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';

import * as productServices from 'services/product';

import { multiplePanelsAllergens } from '../product-media-area/multiple-panel/tab-allergens/queries';
import { getKeyProductIngredients } from '../product-media-area/multiple-panel/tab-ingredients/useProductIngredients';
import { CustomNotification } from 'common/components';

export const productDetailKeys = {
  modules: (productId) => ['product-details', 'modules', parseInt(productId)],
  modulesBrickCode: (brickCode) => [
    'product-details',
    'modules-brickcode',
    brickCode,
  ],
  customProperties: (productId, memberId) => [
    'product-details',
    'custom-properties',
    parseInt(productId),
    parseInt(memberId),
  ],
  memberCustomProperties: (memberId) => [
    'product-details',
    'member-custom-properties',
    parseInt(memberId),
  ],
};

export const useGetProductItemModules = (productId, options = {}) => {
  const queryClient = useQueryClient();

  const { data, status, ...queries } = useQuery({
    queryKey: productDetailKeys.modules(productId),
    queryFn: async () => {
      const response = await productServices.getProductItemModules({
        productId,
      });
      return response?.data;
    },
    enabled: Boolean(productId),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  });

  const refetchDataProductModules = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: productDetailKeys.modules(productId),
    });
  }, [productId, queryClient]);

  return {
    dataProductModules: data ?? {},
    statusFetchProductItem: status,
    refetchDataProductModules,
    ...queries,
  };
};

export const useGetProductItemModulesBrickCode = (brickCode) => {
  const { data, ...queries } = useQuery({
    queryKey: productDetailKeys.modulesBrickCode(brickCode),
    queryFn: async () => {
      const response = await productServices.getProductItemModulesByBrickCode({
        brickCode,
      });
      return response?.data;
    },
    enabled: Boolean(brickCode),
  });

  return { modulesBrickCode: data?.moduleNames ?? [], ...queries };
};

export const useGetProductCustomProperties = (productId, memberId) => {
  const queryClient = useQueryClient();

  const { data, status, ...queries } = useQuery({
    queryKey: productDetailKeys.customProperties(productId, memberId),
    queryFn: async () => {
      const response = await productServices.getProductCustomPropertiesData({
        productId,
      });
      return response?.data;
    },
    enabled: Boolean(productId && memberId),
  });

  const refetchProductCustomPropertiesData = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: productDetailKeys.customProperties(productId, memberId),
    });
  }, [memberId, productId, queryClient]);

  return {
    data,
    refetchProductCustomPropertiesData,
    ...queries,
  };
};

export const useGetMemberProductCustomProperties = (memberId) => {
  const { data, error, ...queries } = useQuery({
    queryKey: productDetailKeys.memberCustomProperties(memberId),
    queryFn: async () => {
      const response = await productServices.getMemberProductCustomProperties({
        memberId,
      });
      return response?.data;
    },
    enabled: Boolean(memberId),
  });

  return { data, error, ...queries };
};

export const useScrollToFoundModule = ({ foundModule, elementByClassName }) => {
  useEffect(() => {
    if (foundModule && elementByClassName?.[0]) {
      elementByClassName[0].scrollIntoView();
    }

    return;
  }, [elementByClassName, foundModule]);
};

export const useSyncProductModules = () => {
  const queryClient = useQueryClient();

  const handleSyncAllergens = (productId) => {
    queryClient.invalidateQueries(
      multiplePanelsAllergens.productAllergens(productId)
    );
  };

  const handleSyncIngredients = (productId) => {
    queryClient.invalidateQueries(getKeyProductIngredients(productId));
  };

  return {
    handleSyncAllergens,
    handleSyncIngredients,
  };
};

export const useSaveProductProperties = (productId) => {
  const { handleSyncAllergens, handleSyncIngredients } =
    useSyncProductModules();

  return useMutation({
    mutationFn: productServices.saveProductItemModules,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        handleSyncAllergens(productId);
        handleSyncIngredients(productId);
        CustomNotification.success('Update product detail successfully');
      } else {
        CustomNotification.error(response?.message ?? 'Something went wrong');
      }
    },
    onError: (error) => {
      CustomNotification.error(error?.message ?? 'Something went wrong');
    },
  });
};
